import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import { useDexService } from '../../../contexts/DexServiceContext';
import { ethers } from 'ethers';
import { LogLevel } from '../../../types/dex';

interface WalletBalanceProps {
  tokenAddress: string;
}

interface WalletBalanceData {
  address: string;
  privateKey: string;
  balance: string;
  nativeTokens: string;
}

interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  balance: string;
  decimals: number;
}

interface CommonToken {
  address: string;
  symbol: string;
  name: string;
}

type CommonTokenMap = Record<number, CommonToken[]>;

const WalletBalance: React.FC<WalletBalanceProps> = ({ tokenAddress }) => {
  const { dexService, config, isInitialized, addLog } = useDexService();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletBalances, setWalletBalances] = useState<WalletBalanceData[]>([]);
  const [selectedWallet, setSelectedWallet] = useState<string>('');
  const [commonTokens, setCommonTokens] = useState<TokenInfo[]>([]);
  const [commonTokensLoading, setCommonTokensLoading] = useState(false);
  const [customTokenLoading, setCustomTokenLoading] = useState(false);
  const [customTokenAddress, setCustomTokenAddress] = useState('');
  const [customTokenInfo, setCustomTokenInfo] = useState<TokenInfo | null>(null);

  // 在组件加载时获取所有钱包余额
  useEffect(() => {
    if (isInitialized && dexService && config) {
      fetchAllWalletBalances();
    }
  }, [isInitialized, dexService, config]);

  // 当tokenAddress变化时获取特定代币余额
  useEffect(() => {
    if (tokenAddress && selectedWallet) {
      fetchTokenBalance(tokenAddress);
    }
  }, [tokenAddress, selectedWallet]);

  // 获取所有钱包的原生代币余额
  const fetchAllWalletBalances = async () => {
    if (!isInitialized || !dexService || !config) {
      const errorMsg = 'DexService未初始化，请先配置服务';
      setError(errorMsg);
      addLog({
        level: LogLevel.ERROR,
        message: errorMsg
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const wallets = config.wallets.filter(wallet => wallet.address);
      
      addLog({
        level: LogLevel.INFO,
        message: `开始获取 ${wallets.length} 个钱包的余额`
      });

      const results = await Promise.allSettled(
        wallets.map(async (wallet) => {
          try {
            if (!wallet.address) throw new Error('钱包地址无效');
            
            const balance = await dexService.getNativeBalance(wallet.address);
            const formattedBalance = ethers.utils.formatEther(balance);
            
            addLog({
              level: LogLevel.SUCCESS,
              message: `钱包 ${wallet.address} 余额: ${formattedBalance} ${config.rpc.symbol}`,
              data: { address: wallet.address, balance: formattedBalance }
            });

            return {
              address: wallet.address,
              privateKey: wallet.privateKey,
              balance: balance.toString(),
              nativeTokens: formattedBalance
            };
          } catch (err: any) {
            console.error(`获取钱包 ${wallet.address} 余额时出错:`, err);
            addLog({
              level: LogLevel.ERROR,
              message: `获取钱包 ${wallet.address} 余额失败: ${err.message || '未知错误'}`,
              data: { address: wallet.address }
            });
            throw err;
          }
        })
      );

      const successfulResults = results
        .filter((result): result is PromiseFulfilledResult<WalletBalanceData> => 
          result.status === 'fulfilled'
        )
        .map(result => result.value);

      const failedResults = results
        .filter((result): result is PromiseRejectedResult => 
          result.status === 'rejected'
        );

      if (failedResults.length > 0) {
        addLog({
          level: LogLevel.WARNING,
          message: `${failedResults.length} 个钱包余额获取失败`
        });
      }

      setWalletBalances(successfulResults);
      
      // 如果有钱包，默认选择第一个
      if (successfulResults.length > 0 && !selectedWallet) {
        setSelectedWallet(successfulResults[0].address);
        addLog({
          level: LogLevel.INFO,
          message: `已自动选择钱包: ${successfulResults[0].address}`
        });
      }
    } catch (err: any) {
      const errorMessage = err.message || '未知错误';
      console.error('获取钱包余额时出错:', err);
      setError(`获取钱包余额时出错: ${errorMessage}`);
      addLog({
        level: LogLevel.ERROR,
        message: `获取钱包余额失败: ${errorMessage}`,
        data: { error: err }
      });
    } finally {
      setLoading(false);
    }
  };

  // 处理钱包选择变更
  const handleWalletChange = (event: SelectChangeEvent<string>) => {
    const newWallet = event.target.value;
    if (!newWallet) {
      addLog({
        level: LogLevel.WARNING,
        message: '选择的钱包地址无效'
      });
      return;
    }
    
    setSelectedWallet(newWallet);
    setCommonTokens([]);
    addLog({
      level: LogLevel.INFO,
      message: `已切换到钱包: ${newWallet}`
    });
  };

  // 获取钱包中常见代币的余额
  const fetchCommonTokens = async () => {
    if (!isInitialized || !dexService || !selectedWallet) {
      addLog({
        level: LogLevel.WARNING,
        message: '无法获取代币余额，请确保已选择钱包且服务已初始化'
      });
      return;
    }

    setCommonTokensLoading(true);

    try {
      const chainId = config?.rpc.chainId || 0;
      addLog({
        level: LogLevel.INFO,
        message: `开始获取常见代币余额，链ID: ${chainId}`
      });

      // 定义当前网络上的常见代币列表
      const commonTokenAddresses: CommonTokenMap = {
        // BSC Testnet
        97: [
          { address: '0xed24fc36d5ee211ea25a80239fb8c4cfd80f12ee', symbol: 'BUSD', name: 'BUSD Token' },
          { address: '0x337610d27c682e347c9cd60bd4b3b107c9d34ddd', symbol: 'USDT', name: 'USDT Token' },
          { address: '0x3Fa2ABC5C14C8280153e52fDf5d3930f076765C5', symbol: 'USDT', name: 'Faker USDT Token' },
          { address: '0x5436184c9CFb48ff62A7C714B75bb0024c77E4ED', symbol: 'Tcash', name: 'Tcash Token' },
        ],
        // BSC Mainnet
        56: [
          { address: '0xe9e7cea3dedca5984780bafc599bd69add087d56', symbol: 'BUSD', name: 'BUSD Token' },
          { address: '0x55d398326f99059ff775485246999027b3197955', symbol: 'USDT', name: 'Tether USD' },
          { address: '0x0e09fabb73bd3ade0a17ecc321fd13a19e81ce82', symbol: 'CAKE', name: 'PancakeSwap Token' },
          { address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', symbol: 'WBNB', name: 'Wrapped BNB' },
          { address: '0x55d398326f99059fF775485246999027B3197955', symbol: 'BSC-USD', name: 'BSC-USD' },
        ],
        // Ethereum Mainnet
        1: [
          { address: '0xdac17f958d2ee523a2206206994597c13d831ec7', symbol: 'USDT', name: 'Tether USD' },
          { address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', symbol: 'USDC', name: 'USD Coin' },
          { address: '0x6b175474e89094c44da98b954eedeac495271d0f', symbol: 'DAI', name: 'Dai Stablecoin' },
        ],
        // Polygon
        137: [
          { address: '0xc2132d05d31c914a87c6611c10748aeb04b58e8f', symbol: 'USDT', name: 'Tether USD' },
          { address: '0x2791bca1f2de4661ed88a30c99a7a9449aa84174', symbol: 'USDC', name: 'USD Coin' },
          { address: '0x8f3cf7ad23cd3cadbd9735aff958023239c6a063', symbol: 'DAI', name: 'Dai Stablecoin' },
        ],
        // 默认几个测试代币
        0: [
          { address: '0x0000000000000000000000000000000000000001', symbol: 'TEST1', name: 'Test Token 1' },
          { address: '0x0000000000000000000000000000000000000002', symbol: 'TEST2', name: 'Test Token 2' },
        ]
      };

      const tokenList = commonTokenAddresses[chainId] || commonTokenAddresses[0];

      addLog({
        level: LogLevel.INFO,
        message: `准备查询 ${tokenList.length} 个常见代币的余额`
      });

      const results = await Promise.allSettled(
        tokenList.map(async (token) => {
          try {
            const { balance, info } = await dexService.getTokenBalanceAndInfo(
              token.address,
              selectedWallet
            );
            
            const formattedBalance = ethers.utils.formatUnits(balance, info.decimals);
            
            addLog({
              level: LogLevel.SUCCESS,
              message: `${info.symbol} 余额: ${formattedBalance}`,
              data: { 
                token: token.address, 
                symbol: info.symbol,
                balance: formattedBalance,
                decimals: info.decimals
              }
            });

            return {
              address: token.address,
              symbol: info.symbol,
              name: info.name,
              balance: formattedBalance,
              decimals: info.decimals
            };
          } catch (err: any) {
            console.error(`获取代币 ${token.symbol} 余额时出错:`, err);
            addLog({
              level: LogLevel.ERROR,
              message: `获取代币 ${token.symbol} 余额失败: ${err.message || '未知错误'}`,
              data: { token: token.address, symbol: token.symbol }
            });
            throw err;
          }
        })
      );

      const successfulResults = results
        .filter((result): result is PromiseFulfilledResult<TokenInfo> => 
          result.status === 'fulfilled'
        )
        .map(result => result.value);

      const failedResults = results
        .filter((result): result is PromiseRejectedResult => 
          result.status === 'rejected'
        );

      if (failedResults.length > 0) {
        addLog({
          level: LogLevel.WARNING,
          message: `${failedResults.length} 个代币余额获取失败`
        });
      }

      setCommonTokens(successfulResults);

    } catch (err: any) {
      console.error('获取常见代币余额时出错:', err);
      addLog({
        level: LogLevel.ERROR,
        message: `获取常见代币余额失败: ${err.message || '未知错误'}`
      });
    } finally {
      setCommonTokensLoading(false);
    }
  };

  // 获取指定代币的余额
  const fetchTokenBalance = async (address: string) => {
    if (!isInitialized || !dexService || !selectedWallet) {
      const errorMsg = '请先选择钱包并确保服务已初始化';
      setError(errorMsg);
      addLog({
        level: LogLevel.ERROR,
        message: errorMsg
      });
      return;
    }

    if (!ethers.utils.isAddress(address)) {
      const errorMsg = '无效的代币地址';
      setError(errorMsg);
      addLog({
        level: LogLevel.ERROR,
        message: errorMsg
      });
      return;
    }

    setCustomTokenLoading(true);
    setError(null);

    try {
      const tokenInfo = await dexService.getTokenInfo(address);
      const balance = await dexService.checkTokenBalance(selectedWallet, address);
      
      const formattedBalance = ethers.utils.formatUnits(balance, tokenInfo.decimals);
      
      const tokenData: TokenInfo = {
        address: address,
        symbol: tokenInfo.symbol,
        name: tokenInfo.name,
        decimals: tokenInfo.decimals,
        balance: formattedBalance
      };

      setCustomTokenInfo(tokenData);
      
      addLog({
        level: LogLevel.SUCCESS,
        message: `获取代币余额成功: ${tokenInfo.symbol}`,
        data: {
          token: tokenInfo.symbol,
          balance: formattedBalance,
          address: address,
          wallet: selectedWallet
        }
      });
    } catch (err: any) {
      const errorMessage = err.message || '未知错误';
      console.error('获取代币余额失败:', err);
      setError(`获取代币余额失败: ${errorMessage}`);
      setCustomTokenInfo(null);
      
      addLog({
        level: LogLevel.ERROR,
        message: `获取代币余额失败: ${errorMessage}`,
        data: { 
          address: address,
          wallet: selectedWallet,
          error: err 
        }
      });
    } finally {
      setCustomTokenLoading(false);
    }
  };

  // 处理查询代币余额
  const handleQueryToken = () => {
    if (!customTokenAddress) {
      setError('请输入代币地址');
      return;
    }
    fetchTokenBalance(customTokenAddress);
  };

  if (!isInitialized || !config) {
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Alert severity="warning">
          DexService未初始化，请先配置服务
        </Alert>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          钱包余额
        </Typography>
        
        {/* 钱包选择器 */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>选择钱包</InputLabel>
          <Select
            value={selectedWallet}
            onChange={handleWalletChange}
            label="选择钱包"
          >
            {walletBalances.map((wallet) => (
              <MenuItem key={wallet.address} value={wallet.address}>
                {wallet.address} ({ethers.utils.formatEther(wallet.balance)} {config.rpc.symbol})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 代币地址输入和查询 */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            label="代币合约地址"
            value={customTokenAddress}
            onChange={(e) => setCustomTokenAddress(e.target.value)}
            error={!!error}
            helperText={error}
          />
          <Button
            variant="contained"
            onClick={handleQueryToken}
            disabled={customTokenLoading || !selectedWallet}
          >
            {customTokenLoading ? <CircularProgress size={24} /> : '查询'}
          </Button>
        </Box>

        {/* 查询结果显示 */}
        {customTokenInfo && (
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {customTokenInfo.name} ({customTokenInfo.symbol})
            </Typography>
            <Typography variant="body1">
              余额: {customTokenInfo.balance}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              合约地址: {customTokenInfo.address}
            </Typography>
          </Card>
        )}

        {/* 钱包列表及原生代币余额 */}
        <TableContainer component={Paper} sx={{ bgcolor: 'background.default', mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>钱包地址</TableCell>
                <TableCell>私钥</TableCell>
                <TableCell align="right">原生代币余额</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : (
                walletBalances.map((wallet) => (
                  <TableRow key={wallet.address}>
                    <TableCell 
                      component="th" 
                      scope="row"
                      sx={{ 
                        fontFamily: 'monospace', 
                        cursor: 'pointer', 
                        '&:hover': { textDecoration: 'underline' },
                        bgcolor: selectedWallet === wallet.address ? 'action.selected' : 'inherit'
                      }}
                      onClick={() => setSelectedWallet(wallet.address)}
                    >
                      {wallet.address.slice(0, 8)}...{wallet.address.slice(-6)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {wallet.privateKey.slice(0, 4)}...{wallet.privateKey.slice(-4)}
                    </TableCell>
                    <TableCell align="right">
                      {wallet.nativeTokens} {config.rpc.symbol}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 查看选定钱包的常见代币 */}
        <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              查看常见代币余额
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>选择钱包</InputLabel>
                <Select
                  value={selectedWallet}
                  label="选择钱包"
                  onChange={handleWalletChange}
                >
                  {walletBalances.map((wallet) => (
                    <MenuItem key={wallet.address} value={wallet.address}>
                      {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                size="small"
                onClick={fetchCommonTokens}
                disabled={!selectedWallet || commonTokensLoading}
              >
                {commonTokensLoading ? <CircularProgress size={20} /> : '查询常见代币'}
              </Button>
            </Box>
          </Box>

          {commonTokens.length > 0 && (
            <TableContainer component={Paper} sx={{ bgcolor: 'background.default' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>代币</TableCell>
                    <TableCell>合约地址</TableCell>
                    <TableCell align="right">余额</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commonTokens.map((token) => (
                    <TableRow key={token.address}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip label={token.symbol} size="small" sx={{ mr: 1 }} />
                          {token.name}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {token.address.slice(0, 8)}...{token.address.slice(-6)}
                      </TableCell>
                      <TableCell align="right">
                        {token.balance}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        <Alert severity="info">
          提示：如需查询特定代币的余额，请使用"代币余额检查"功能。
        </Alert>
      </Paper>
    </Box>
  );
};

export default WalletBalance;
